import React, { useState } from 'react';
import { Form, Input } from 'antd';
import { useHistory, useParams } from 'react-router-dom';

import InlineAlert from '../../../components/common/InlineAlert';
import {
  DangerButton,
  DialogBody,
  DialogFooter,
  DialogLede,
  DialogModal,
  DialogNote,
  DialogTitle,
  GhostButton,
} from '../../../components/common/DialogModal';
import { BookingForm } from '../../../components/NewBooking/BookingForm';
import { recordActivity } from '../../../API/workflow';
import { deleteAppointment } from '../../../API/appointment';
import {
  ROUTE_PREFIX_BOOKING,
  ROUTE_PREFIX_PRE_APPT,
  ROUTES,
  ROUTES_PRE_APPT,
} from '../../../constants/routes';
import { ACTIVITY_TYPE, STATUS } from '../../../constants';
import { CONTACT_PHONE } from '../../../constants/business';

const { TextArea } = Input;
const { DEFAULT, PENDING, SUCCESS, FAILURE } = STATUS;

const PAGE_TYPE = {
  NEW_BOOKING: 'NEW-BOOKING',
  PRE_APPT: 'PRE-APPT',
};

const SERVER_ERROR_MESSAGE =
  'Something went wrong on server side, please try again';
const REASON_MAX_LENGTH = 1000;

const CancelModal = ({
  isCancelModalOpen,
  toggleCancelModal,
  pageType = PAGE_TYPE.NEW_BOOKING,
}) => {
  const [form] = Form.useForm();
  const [status, setStatus] = useState(DEFAULT);
  const { workflowId } = useParams();
  const history = useHistory();

  const cancelPreApptAppointment = async () => {
    const { status: resultStatus } = await deleteAppointment(workflowId);

    if (resultStatus !== SUCCESS) return false;

    toggleCancelModal(false);
    history.push(
      `${ROUTE_PREFIX_PRE_APPT}/${workflowId}${ROUTES_PRE_APPT.NO_APPT}`
    );

    return true;
  };

  const cancelBookingRequest = async (reason) => {
    const { status: resultStatus } = await recordActivity(workflowId, {
      name: ACTIVITY_TYPE.CANCEL,
      reason,
    });

    if (resultStatus !== SUCCESS) return false;

    toggleCancelModal(false);
    history.push(
      `${ROUTE_PREFIX_BOOKING}/${workflowId}${ROUTES.NOTIFICATION}?type=${ACTIVITY_TYPE.CANCEL}`
    );

    return true;
  };

  const handleSubmit = async () => {
    setStatus(PENDING);

    const isCancelled =
      pageType === PAGE_TYPE.PRE_APPT
        ? await cancelPreApptAppointment()
        : await cancelBookingRequest(form.getFieldValue('reason'));

    setStatus(isCancelled ? SUCCESS : FAILURE);
  };

  return (
    <DialogModal
      visible={isCancelModalOpen}
      footer={null}
      width={520}
      onCancel={() => toggleCancelModal(false)}
    >
      <DialogBody>
        <DialogTitle>
          Are you sure you want to cancel your appointment?
        </DialogTitle>

        <DialogLede>
          By agreeing to cancel your appointment you are acknowledging you no
          longer wish to see one of our specialists or allied health
          professionals.
        </DialogLede>

        <BookingForm form={form} layout="vertical">
          <Form.Item name="reason" label="Reason for cancelling">
            <TextArea
              rows={4}
              maxLength={REASON_MAX_LENGTH}
              showCount
              placeholder="Reason for cancelling..."
            />
          </Form.Item>
        </BookingForm>

        {status === FAILURE && (
          <InlineAlert>{SERVER_ERROR_MESSAGE}</InlineAlert>
        )}

        <DialogNote>
          For further information please press the chat icon or call{' '}
          <b>{CONTACT_PHONE}</b> to speak with us.
        </DialogNote>
      </DialogBody>

      <DialogFooter>
        <GhostButton type="text" onClick={() => toggleCancelModal(false)}>
          Keep my appointment
        </GhostButton>
        <DangerButton onClick={handleSubmit} loading={status === PENDING}>
          Confirm
        </DangerButton>
      </DialogFooter>
    </DialogModal>
  );
};

export default CancelModal;
